import { call, put, select } from 'redux-saga/effects';
import type { Action } from '@reduxjs/toolkit';
import { signalingQuizLaunched } from 'features/quizzes/actions';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import type { SidePanelType } from 'features/side-panels/types';
import { selectSidePanel, sidePanelOpened } from 'features/side-panels/sidePanelsSlice';
import { addActionNotification } from 'features/notifications/action-notifications/addActionNotification';
import i18n from 'i18n';
import { FeatureFlag } from 'features/feature-set/types';
import { selectIsSystemSession } from 'features/application/selectors';
import { quizLaunchedAtSet } from 'features/quizzes/quizzesSlice';
import { isQuiz } from 'features/quizzes/types';
import { resolveEventUserSaga } from 'features/users/utils/resolveEventUser';
import { User } from 'features/users/types';
import { EventUser } from 'services/signaling';

export function* onQuizLaunchedSaga(action: ReturnType<typeof signalingQuizLaunched>) {
  // Set the launch timestamp for timer synchronization
  yield put(quizLaunchedAtSet(Date.now()));

  const isSystemSession: boolean = yield select(selectIsSystemSession);
  if (isSystemSession) {
    yield put(sidePanelOpened('polls'));
    return;
  }

  // Don't show a notification to moderators
  const canManagePolls: boolean = yield call(hasPermissionsSaga, PermissionTypes.managePolls);
  if (canManagePolls) {
    return;
  }

  const canTakePolls: boolean = yield call(hasPermissionsSaga, PermissionTypes.takePolls);
  if (!canTakePolls) {
    return;
  }

  const openedPanel: SidePanelType = yield select(selectSidePanel);
  if (openedPanel === 'polls') {
    // Show only if the polls panel is not opened
    return;
  }

  const { initiator, data } = action.payload;
  const resolvedInitiator: User | EventUser | undefined = yield call(
    resolveEventUserSaga,
    initiator
  );
  const initiatorName = resolvedInitiator?.name;

  if (isQuiz(data) && data.submitted) {
    return;
  }

  const actionNotificationThunk: Action = yield call(
    addActionNotification,
    {
      content: i18n.t('notifications:quizzes.request_to_take.content', {
        username: initiatorName,
        defaultValue: '{{username}} launched a quiz',
      }),
      okText: i18n.t('notifications:quizzes.request_to_take.btn_ok_label'),
      okAction: sidePanelOpened('polls'),
      initiator: resolvedInitiator,
      scope: [FeatureFlag.polls, 'quiz_request_take'],
    },
    {
      toastId: `quiz_request_take_${data.id}`,
    }
  );

  yield put(actionNotificationThunk);
}
